import {createContext, type PropsWithChildren, use, useCallback, useMemo} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {dataRetryPolicy, fetchBackend} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import {EventHappening} from "@/lib/types";
import {useGlobalSearchParams} from "expo-router";

const EventContext = createContext<{
    event?: EventHappening;
    eventLoading: boolean;
    eventError: Error | null;
    refreshEvent: () => any;
}>({
    event: undefined,
    eventLoading: true,
    eventError: null,
    refreshEvent: () => {
    },
});

export function useEvent() {
    const value = use(EventContext);
    if (!value) {
        throw new Error('useEvent must be wrapped in a <EventProvider />');
    }
    return value;
}

export function EventProvider({children}: PropsWithChildren) {
    const queryClient = useQueryClient();
    const {session, ...sessionParams} = useSession();

    const {event_id} = useGlobalSearchParams();
    const queryKey = useMemo(() => ['event', event_id, session], [event_id, session]);
    const queryKeyAll = useMemo(() => ['event', session], [session]);

    const refreshEvent = useCallback(async () => {
        await queryClient.invalidateQueries({queryKey});
        await queryClient.invalidateQueries({queryKey: queryKeyAll});
    }, [queryClient, queryKey, queryKeyAll]);

    const {data, isLoading, error} = useQuery<EventHappening>({
        queryKey,
        queryFn: () => fetchBackend(`/event/${event_id}`, "GET", session),
        staleTime: 1000 * 60 * 15,
        retry: dataRetryPolicy,
        enabled: !!event_id && !sessionParams.isLoading,
    })

    return (
        <EventContext.Provider
            value={{
                event: data,
                refreshEvent,
                eventLoading: isLoading,
                eventError: error,
            }}>
            {children}
        </EventContext.Provider>
    );
}